import { TableContainer } from 'ui/StyledComponents/Tables'
import FloatingActionBtn from 'ui/components/FloatingActionBtn'
import { useIntl } from 'react-intl'
import {
	TableRow,
	TableColumn,
	DataTable,
	TableHeader,
	TableBody,
	ListItem,
	FontIcon,
} from 'react-md'
import { useMemo, useState } from 'react'
import { chartOfAccountsHeaders } from '../constants'
import ChartOfAccountDialog from './ChartOfAccountDialog'
import { useDispatch } from 'react-redux'
import { push } from 'connected-react-router'
import config from 'config'
import * as notifyActions from 'state/notifyActions'
import useDialogs from 'ui/components/dialogs/useDialogs'
import LoadingState from 'ui/components/LoadingState'
import { LoadingContainer } from '../../components'
import type { UseChartOfAccountCategoryResult } from './useChartOfAccountsCategories'
import type { TalousChartOfAccount } from './validation'
import type { ChartOfAccountCategory } from '../types'
import { getLowestLevelCategory } from './utils'
import ChartOfAccountTableRow from './ChartOfAccountTableRow'
import Breadcrumbs from 'ui/components/Breadcrumbs'
import styled from 'styled-components'
import type { UseTalousChartOfAccountsResult } from './useTalousChartsOfAccounts'
import type { useVatsResult } from '../../Accounting/Voucher/useVats'

const HASH_CHART_OF_ACCOUNTS = config.routeHashes.chartOfAccounts

const AccountTableList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`

const StyledDataTable = styled(DataTable)`
	table-layout: fixed;
	width: 100%;

	td {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	th,
	td {
		padding: 8px 16px;
	}
`

const getColumnWidth = (key: string): string => {
	switch (key) {
		case 'name':
			return '40%'
		default:
			// Remaining 60% distributed among 6 other columns = 10% each
			return '10%'
	}
}

type Props = {
	selectedFinanceCompanyUUID: string
	useChartOfAccounts: UseTalousChartOfAccountsResult
	useChartOfAccountCategories: UseChartOfAccountCategoryResult
	useVats: useVatsResult
	chartOfAccountUUID: string | null
}

const ChartOfAccounts = ({
	selectedFinanceCompanyUUID,
	useChartOfAccounts,
	useChartOfAccountCategories,
	useVats,
	chartOfAccountUUID,
}: Props) => {
	const intl = useIntl()
	const dispatch = useDispatch()
	const { confirm } = useDialogs()
	const [processing, setProcessing] = useState<Boolean>(false)
	const [addDialogVisible, setDialogVisible] = useState<boolean>(false)
	const [selectedAccount, setSelectedAccount] =
		useState<TalousChartOfAccount | null>(null)

	const { chartOfAccounts, loadingChartOfAccounts } = useChartOfAccounts
	const { chartOfAccountCategories, loading: loadingChartOfAccountCategories } =
		useChartOfAccountCategories
	const { vats } = useVats

	const showAddChartOfAccountDialog = () => {
		setSelectedAccount(null)
		setDialogVisible(true)
	}

	const hideAddChartofAccountDialog = () => {
		setDialogVisible(false)
		if (chartOfAccountUUID) {
			dispatch(push(window.location.pathname + HASH_CHART_OF_ACCOUNTS))
		}
	}

	const handleRowClick = (account: TalousChartOfAccount) => {
		setSelectedAccount(account)
		setDialogVisible(true)
	}

	const renderFAB = () => {
		return (
			<FloatingActionBtn
				iconName={'add'}
				text={intl.formatMessage({
					defaultMessage: 'UUSI TILI',
					description: 'Add new account for char of accounts',
				})}
				action={showAddChartOfAccountDialog}
			/>
		)
	}

	const createChartOfAccount = () => {
		setProcessing(true)
		setProcessing(false)
		dispatch(
			notifyActions.success(
				intl.formatMessage({
					defaultMessage: 'Tili on lisätty.',
					description: 'Notification text chart of account has been added',
				}),
			),
		)
	}

	// TODO: Update the chart of account field with actual API call
	const updateChartOfAccountField = async () => {
		setProcessing(true)
		setProcessing(false)
	}

	// TODO: Delete the chart of account with actual API call
	const deleteChartOfAccount = async () => {
		const confirmMessage = intl.formatMessage({
			defaultMessage: 'Oletko varma, että haluat poistaa tilin?',
			description: 'Confirm message for the delete chart of account.',
		})
		if (!(await confirm(confirmMessage))) {
			return
		}

		setProcessing(true)
		setProcessing(false)

		dispatch(
			notifyActions.success(
				intl.formatMessage({
					defaultMessage: 'Tili on poistettu.',
					description: 'Notification text chart of account has been deleted',
				}),
			),
		)
		return true
	}

	const renderTableHeaders = () => {
		return (
			<TableRow>
				{chartOfAccountsHeaders
					.filter((h) => h.show)
					.map((header) => (
						<TableColumn
							key={header.key}
							style={{ width: getColumnWidth(header.key) }}
						>
							<p className="text-strong text-subtle">
								{header?.title ? header.title(intl) : ''}
							</p>
						</TableColumn>
					))}
				<TableColumn
					key={'children-header'}
					style={{ width: '10%' }}
				></TableColumn>
			</TableRow>
		)
	}

	const accountsByCategory = useMemo(() => {
		// Group accounts by category UUID, and store the breadcrumb for each group
		const accounts: Record<
			string,
			{ accounts: TalousChartOfAccount[]; breadcrumb: ChartOfAccountCategory[] }
		> = {}

		// If no categories or accounts, return empty object
		if (!chartOfAccountCategories || !chartOfAccounts) return undefined

		chartOfAccounts.forEach((account) => {
			const { category, chain } = getLowestLevelCategory(
				account.code,
				chartOfAccountCategories,
			)
			if (category) {
				if (!accounts[category.uuid]) {
					accounts[category.uuid] = { accounts: [], breadcrumb: chain }
				}
				accounts[category.uuid].accounts.push(account)
			}
		})

		return accounts
	}, [chartOfAccountCategories, chartOfAccounts])

	return (
		<TableContainer style={{ paddingRight: '1rem' }}>
			{loadingChartOfAccounts ||
				(loadingChartOfAccountCategories && (
					<LoadingContainer>
						<LoadingState />
					</LoadingContainer>
				))}

			{accountsByCategory && Object.keys(accountsByCategory).length > 0 ? (
				<AccountTableList>
					{Object.entries(accountsByCategory).map(
						([catUuid, { accounts, breadcrumb }]) => (
							<div key={catUuid}>
								<Breadcrumbs
									items={breadcrumb.map((category) => category.name)}
								/>
								<StyledDataTable
									baseId={`chart-of-accounts-${catUuid}`}
									plain
									style={{
										background: 'white',
										maxHeight: 'calc(100vh - 205px)',
									}}
								>
									<TableHeader
										style={{
											position: 'sticky',
											top: 0,
											zIndex: 1,
											background: 'white',
										}}
									>
										{renderTableHeaders()}
									</TableHeader>
									<TableBody>
										{accounts.map((chartOfAccount) => (
											<ChartOfAccountTableRow
												key={`${catUuid}-${chartOfAccount.id}-${chartOfAccount.code}`}
												entity={chartOfAccount}
												vats={vats}
												menuItems={[
													<ListItem
														key={`edit-${chartOfAccount.id}`}
														primaryText={intl.formatMessage({
															defaultMessage: 'Poista tili',
															description: 'Delete account',
														})}
														leftIcon={<FontIcon>delete</FontIcon>}
														onClick={() => deleteChartOfAccount()}
														className="md-divider-border md-divider-border--right"
													/>,
												]}
												children={null}
												processing={processing}
												headers={chartOfAccountsHeaders}
												handleOnClick={() => handleRowClick(chartOfAccount)}
												updateField={updateChartOfAccountField}
											/>
										))}
									</TableBody>
								</StyledDataTable>
							</div>
						),
					)}
				</AccountTableList>
			) : (
				<div style={{ textAlign: 'center', marginTop: '2rem' }}>
					<p>
						{intl.formatMessage({
							defaultMessage: 'Tilejä ei löytynyt. Aloita luomalla uusi tili.',
							description: 'Empty state text for no accounts available',
						})}
					</p>
				</div>
			)}
			<ChartOfAccountDialog
				id={chartOfAccountUUID}
				useChartOfAccounts={useChartOfAccounts}
				visible={typeof chartOfAccountUUID === 'string' || addDialogVisible}
				onHide={hideAddChartofAccountDialog}
				selectedFinanceCompanyUUID={selectedFinanceCompanyUUID}
				useChartOfAccountCategories={useChartOfAccountCategories}
				onSave={() => createChartOfAccount()}
				selectedAccount={selectedAccount} // Pass the selected account to the dialog
			/>

			{renderFAB()}
		</TableContainer>
	)
}

export default ChartOfAccounts
